import { ComponentDefinitionIds } from "./componentDefinitionIds";
import manifest from "./chat-tools.manifest.json";

/**
 * D1 — shared declaration/browser-fulfilled chat block/tool manifest accessor.
 *
 * This module is the FRONTEND view of the cross-runtime manifest defined in
 * `chat-tools.manifest.json`. The SAME JSON file is embedded into the .NET
 * `PivotalGroup.Application` assembly (see `Chat/Tools/ChatToolManifestProvider`)
 * so declaration tools and Vue inline renderers stay a single source of truth.
 * Server-executed tools are registered in `Chat/Tools/Executable` and merged
 * into the admin tool catalog by the API; do not fork them into this manifest.
 */

export type ChatToolKind = "interactive" | "display" | "editor";
export type ChatToolStatus = "available" | "planned";
export type ChatToolSurface = "candidate" | "admin" | "both";
export type ChatToolAnswerType = "single-choice" | "multi-choice" | "text" | "number";

/** Provider-safe JSON Schema subset (no $ref/oneOf/anyOf/allOf/conditionals). */
export interface ChatToolJsonSchema {
  type: string;
  description?: string;
  properties?: Record<string, ChatToolJsonSchema>;
  items?: ChatToolJsonSchema;
  required?: string[];
  enum?: Array<string | number>;
  additionalProperties?: boolean;
}

export interface ChatToolAnswerMeta {
  type: ChatToolAnswerType;
  valuePath: string;
}

export interface ChatToolDescriptor {
  /** snake_case agent function name (e.g. `render_radio_group`). */
  name: string;
  kind: ChatToolKind;
  status: ChatToolStatus;
  surface: ChatToolSurface;
  /** Building-block mimetype rendered for this tool's output; null for editor/no-render tools. */
  componentId: string | null;
  title: string;
  description: string;
  requiresResult: boolean;
  confirmBeforeWrite: boolean;
  /** Interactive answer mapping; null when the tool collects no answer. */
  answer: ChatToolAnswerMeta | null;
  /** Model-supplied argument schema. Field names MUST match the real block data. */
  argsSchema: ChatToolJsonSchema;
  /** Minimal user-submitted result schema; null for display/no-result tools. */
  resultSchema: ChatToolJsonSchema | null;
}

export interface ChatToolManifest {
  version: number;
  description?: string;
  tools: ChatToolDescriptor[];
}

const typedManifest = manifest as unknown as ChatToolManifest;

export const chatToolManifest: ChatToolManifest = typedManifest;
export const chatTools: ChatToolDescriptor[] = typedManifest.tools;

const byName = new Map<string, ChatToolDescriptor>(
  chatTools.map((tool) => [tool.name, tool]),
);

const byComponentId = new Map<string, ChatToolDescriptor>(
  chatTools
    .filter((tool): tool is ChatToolDescriptor & { componentId: string } => tool.componentId !== null)
    .map((tool) => [tool.componentId, tool]),
);

export function getChatTool(name: string): ChatToolDescriptor | undefined {
  return byName.get(name);
}

export function getToolByComponentId(componentId: string): ChatToolDescriptor | undefined {
  return byComponentId.get(componentId);
}

const knownComponentIds = new Set<string>(Object.values(ComponentDefinitionIds));

/**
 * Validates the manifest against the cross-runtime contract. Returns the list of
 * problems (empty when valid). Called at module load in dev to fail loud on drift;
 * a backend xUnit test enforces the same rules in CI.
 */
export function validateChatToolManifest(): string[] {
  const errors: string[] = [];
  const seen = new Set<string>();

  for (const tool of chatTools) {
    if (seen.has(tool.name)) {
      errors.push(`Duplicate tool name: ${tool.name}`);
    }
    seen.add(tool.name);

    if (tool.kind === "interactive") {
      if (!tool.componentId) {
        errors.push(`Interactive tool '${tool.name}' must declare a componentId.`);
      }
      if (!tool.answer) {
        errors.push(`Interactive tool '${tool.name}' must declare answer metadata.`);
      }
      if (!tool.resultSchema) {
        errors.push(`Interactive tool '${tool.name}' must declare a resultSchema.`);
      }
    }

    if (tool.componentId && !knownComponentIds.has(tool.componentId)) {
      errors.push(
        `Tool '${tool.name}' references unknown componentId '${tool.componentId}' (not in ComponentDefinitionIds).`,
      );
    }
  }

  return errors;
}

if (import.meta.dev) {
  const problems = validateChatToolManifest();
  if (problems.length > 0) {
    console.error("[chat-tools] manifest validation failed:\n" + problems.join("\n"));
  }
}
